import api from '../../lib/api';
import type {
  AuditDetail,
  AuditFinding,
  AuditPayload,
  AuditProgram,
  AuditReport,
  AuditTeamMember,
  FindingPayload,
  PaginatedAudits,
} from './types';

export interface ListAuditsParams {
  page?: number;
  pageSize?: number;
  type?: string;
  status?: string;
  year?: number;
  programId?: string;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
}

export const fetchAuditPrograms = async (): Promise<AuditProgram[]> => {
  const { data } = await api.get<{ data: AuditProgram[] }>('audits/programs');
  return data.data;
};

export const createAuditProgram = async (payload: {
  year: number;
  objectives?: string | null;
}): Promise<AuditProgram> => {
  const { data } = await api.post<AuditProgram>('audits/programs', payload);
  return data;
};

export const fetchAudits = async (params: ListAuditsParams = {}): Promise<PaginatedAudits> => {
  const { data } = await api.get<PaginatedAudits>('audits', { params });
  return data;
};

export const fetchAudit = async (id: string): Promise<AuditDetail> => {
  const { data } = await api.get<AuditDetail>(`audits/${id}`);
  return data;
};

export const createAudit = async (payload: AuditPayload) => {
  const { data } = await api.post('audits', payload);
  return data;
};

export const updateAudit = async (id: string, payload: Partial<AuditPayload>) => {
  const { data } = await api.put(`audits/${id}`, payload);
  return data;
};

export const loadChecklistTemplate = async (auditId: string) => {
  const { data } = await api.post<{ data: unknown[] }>(`audits/${auditId}/checklist`, {
    template: 'iso9001',
  });
  return data.data;
};

export const addChecklistItem = async (
  auditId: string,
  payload: { clause: string; question: string; order?: number },
) => {
  const { data } = await api.post<{ data: unknown[] }>(`audits/${auditId}/checklist`, payload);
  return data.data;
};

export const updateChecklistItem = async (
  itemId: string,
  payload: { result?: string | null; evidence?: string | null },
) => {
  const { data } = await api.patch(`audits/checklist/${itemId}`, payload);
  return data;
};

export const addTeamMember = async (
  auditId: string,
  payload: { userId?: number; externalName?: string; role?: string },
): Promise<AuditTeamMember> => {
  const { data } = await api.post<AuditTeamMember>(`audits/${auditId}/team`, payload);
  return data;
};

export const removeTeamMember = async (memberId: string): Promise<void> => {
  await api.delete(`audits/team/${memberId}`);
};

export const createFinding = async (
  auditId: string,
  payload: FindingPayload,
): Promise<AuditFinding> => {
  const { data } = await api.post<AuditFinding>(`audits/${auditId}/findings`, payload);
  return data;
};

export const updateFinding = async (
  findingId: string,
  payload: Partial<FindingPayload>,
): Promise<AuditFinding> => {
  const { data } = await api.patch<AuditFinding>(`audits/findings/${findingId}`, payload);
  return data;
};

export const promoteFindingToRisk = async (
  findingId: string,
): Promise<{ id: string; code: string; title: string }> => {
  const { data } = await api.post<{ id: string; code: string; title: string }>(
    `audits/findings/${findingId}/promote-risk`,
  );
  return data;
};

export const escalateFindingToNc = async (
  findingId: string,
): Promise<{ finding: AuditFinding; qmsRecordId: string }> => {
  const { data } = await api.post<{ finding: AuditFinding; qmsRecordId: string }>(
    `audits/findings/${findingId}/to-nc`,
  );
  return data;
};

export const fetchAuditReport = async (auditId: string): Promise<AuditReport> => {
  const { data } = await api.get<AuditReport>(`audits/${auditId}/report`);
  return data;
};
